import { Controller } from '../utils/controller';
import { CartDrawer } from '../webcomponents/CartDrawer/CartDrawer';

type CartModeType = 'modal' | 'overlay';

interface IToggleCartModeEvent {
  detail: {
    cartMode: CartModeType;
    collectionId?: number;
    skuId?: string[];
    offerCode?: string;
    quantity?: number;
    error?: boolean;
  };
}

export default class extends Controller {
  static targets = ['cartItemsList', 'quantity', 'drawerComponent'];
  static values = {
    mode: 'overlay',
    dataEndpoint: '/cart/v3_0/drawer/',
    errorEndpoint: '/cart/v3_0/drawer/error',
    isLoading: false
  };

  declare cartItemsListTarget: HTMLElement;
  declare hasCartItemsListTarget: boolean;

  declare quantityTarget: HTMLElement;
  declare hasQuantityTarget: boolean;

  declare drawerComponentTargets: HTMLElement[];
  declare hasDrawerComponentTarget: boolean;

  declare modeValue: CartModeType;
  declare dataEndpointValue: string;
  declare errorEndpointValue: string;
  declare isLoadingValue: boolean;

  connect(): void {
    this.updateContent().then(() => {
      if (this.hasDrawerComponentTarget) {
        for (const component of this.drawerComponentTargets) {
          const elementMode =
            component?.getAttribute('data-cart-drawer-mode') ||
            component?.getAttribute('slot') ||
            '';

          if (elementMode !== this.modeValue) {
            component.classList.add('hidden');
          } else {
            component.classList.remove('hidden');
          }
        }
      }
    });
  }

  open(event: IToggleCartModeEvent) {
    if (!this.isLoadingValue) {
      this.toggleCartMode(event).then(() => {
        setTimeout(() => {
          this.pageScroll('disable');
          this.displayComponents('enable');
        }, this.timeOut);
      });
    }
  }

  close() {
    this.cartDrawerAnalytics('close_icon', 'dismiss');
    setTimeout(() => {
      this.pageScroll('enable');
      this.displayComponents('disable');
    }, this.timeOut);
  }

  redirect({ params: { url, analytics } }) {
    this.cartDrawerAnalytics(analytics, 'click');
    if (url) {
      window.location.href = url;
    } else {
      this.pageScroll('enable');
      this.displayComponents('disable');
    }
  }

  private pageScroll(action: 'enable' | 'disable') {
    const hasVulcanStyles = window._platform === 'vulcan';
    const scrollClass = hasVulcanStyles ? 'disable-scroll--vulcan' : 'disable-scroll';

    if (action === 'disable') {
      document.body.classList.add(scrollClass);
      document.documentElement.classList.add(scrollClass);
    } else {
      document.body.classList.remove(scrollClass);
      document.documentElement.classList.remove(scrollClass);
    }
  }

  private cartDrawerAnalytics(drawerLabel: string, drawerAction: string) {
    const cartDrawerType = this.modeValue === 'overlay' ? 'cart_dropdown' : 'cart_overlay';
    if (cartDrawerType) {
      const cartDrawerEvent = new CustomEvent('cart.drawer.clicked', {
        detail: {
          cartDrawerCta: drawerLabel,
          cartDrawerAction: drawerAction,
          cartDrawerCategory: cartDrawerType
        }
      });
      window.dispatchEvent(cartDrawerEvent);
    }
  }

  private displayComponents(action: 'enable' | 'disable') {
    for (const element of (this.element as CartDrawer).innerElements) {
      const elementName = element?.getAttribute('data-cart-element') || '';
      const shouldUpdate = [`${this.modeValue}`, 'background'].includes(elementName);

      if (shouldUpdate) {
        action === 'enable'
          ? element?.classList.remove('hidden')
          : element?.classList.add('hidden');
      }
    }
  }

  async updateContent(params?: IToggleCartModeEvent['detail']) {
    const newContent = await this.fetch(params);
    if (newContent) {
      this.repaint(newContent);
      this.updateCartIconCount();
    }
    this.toggleComponents();
  }

  private async fetch(params?: IToggleCartModeEvent['detail']) {
    this.isLoadingValue = true;

    const { collectionId, offerCode, skuId = [], quantity = 1, error = false } = params || {};

    const shouldIncludeParams = this.modeValue === 'modal' && params && error === false;

    const idParams = offerCode ? `&offerCode=${offerCode}` : `&skuId=${skuId.toString()}`;
    const collectionParams = collectionId ? `&collectionId=${collectionId.toString()}` : '';

    const updateParams = shouldIncludeParams
      ? `${idParams}&quantity=${quantity}${collectionParams}`
      : '';
    const baseUrl = error ? this.errorEndpointValue : this.dataEndpointValue;

    const url = `${baseUrl}?mode=${this.modeValue}${updateParams}`;

    return (
      fetch(url)
        .then((response) => response.text())
        // eslint-disable-next-line no-console
        .catch((error) => console.warn('Could not fetch cart drawer', { error, url }))
        .finally(() => (this.isLoadingValue = false))
    );
  }

  private repaint(html: string) {
    const prevScrollPos = this.hasCartItemsListTarget ? this.cartItemsListTarget.scrollTop : 0;
    this.element.innerHTML = html;

    requestAnimationFrame(() => {
      if (this.hasCartItemsListTarget) {
        this.cartItemsListTarget.scrollTop = prevScrollPos;
      }
    });
  }

  private updateCartIconCount() {
    if (this.hasQuantityTarget) {
      const itemsCount = this.quantityTarget.getAttribute('data-value');
      try {
        const event = new CustomEvent('setCartIconCount', { detail: { itemsCount } });
        window.dispatchEvent(event);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Could not dispatch event', error);
      }
    }
  }

  private async toggleCartMode(event: IToggleCartModeEvent) {
    const newMode = event?.detail?.cartMode || 'overlay';
    const hasToggled = this.modeValue !== newMode || newMode === 'modal';

    if (hasToggled) {
      this.modeValue = newMode;
      if (newMode === 'modal') {
        await this.updateContent(event?.detail);
      }
      this.toggleComponents();
    }
  }

  private toggleComponents() {
    if (this.hasDrawerComponentTarget) {
      for (const component of this.drawerComponentTargets) {
        const elementMode =
          component?.getAttribute('data-cart-drawer-mode') || component?.getAttribute('slot') || '';

        if (elementMode !== this.modeValue) {
          component.classList.add('hidden');
        } else {
          component.classList.remove('hidden');
        }
      }
    }
  }

  private get timeOut() {
    return this.modeValue === 'overlay' ? 500 : 0;
  }
}
